import * as React from "react"

import Button from "../../design-system/button"
import Field from "../../design-system/field"
import Form from "../../design-system/form"
import Input from "../../design-system/input"
import * as analytics from "../../utils/analytics"
import { classNames } from "../../utils/class-names"
import { getUserEmail } from "../../utils/local-storage-user-info"
import { ApiResponse } from "../../utils/requests/types"
import { validateEmail } from "../../utils/validation"
import * as styles from "./index.module.scss"

interface Props {
  buttonText: string
  onSubmitSuccess: (email: string) => void
  submitForm: (
    fields: [{ name: "email"; value: string }],
  ) => Promise<ApiResponse>
  formName: string
  buttonLocation?: "footer" | "body" | "hero" | "modal"
  className?: string
  placeholderText: string
}

const EmailForm = ({
  buttonText,
  formName,
  onSubmitSuccess,
  submitForm,
  className,
  buttonLocation = "body",
  placeholderText,
}: Props) => {
  const [email, setEmail] = React.useState<string>(getUserEmail())
  const [loading, setLoading] = React.useState<boolean>(false)
  const [apiErrorMessage, setApiErrorMessage] = React.useState("")
  const [localErrorMessage, setLocalErrorMessage] = React.useState("")
  const [submitted, setSubmitted] = React.useState(false)

  const handleInputChange = (value: string) => {
    setEmail(value)
    const error = validateEmail(value)
    setLocalErrorMessage(error || "")
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSubmitted(true)
    if (localErrorMessage) {
      return
    }
    setLoading(true)

    const properties = {
      // TODO(localization): post-launch: stable english reference on buttonName
      buttonName: buttonText,
      buttonLocation,
      buttonPosition: "1",
    }
    analytics.buttonClicked(properties)

    const response = await submitForm([{ name: "email", value: email }])
    const { success, errors } = response

    analytics.trackFormSubmissionResult({
      formName,
      formData: { email },
      errorMessage: success ? undefined : errors,
    })
    if (response.success) {
      onSubmitSuccess?.(email)
    } else {
      setApiErrorMessage(errors || "An error occurred.")
    }

    setLoading(false)
  }

  return (
    <Form
      className={classNames(styles.form, className)}
      onSubmit={handleSubmit}
    >
      <Field
        className={styles.field}
        errorMessage={
          submitted ? apiErrorMessage || localErrorMessage : undefined
        }
      >
        <Input
          className={styles.input}
          name="email"
          value={email}
          placeholder={placeholderText}
          setInputValue={handleInputChange}
          id={`email-input-${formName}`}
          onChange={e => handleInputChange(e.target.value)}
          data-testid={`email-input-${formName}`}
          required
        />
      </Field>
      <Button
        className={styles.button}
        type="submit"
        loading={loading}
        data-testid={`submit-button-${formName}`}
        fullWidth
      >
        {buttonText}
      </Button>
    </Form>
  )
}

export default EmailForm
